import type { AuditTrailResult } from '@whinerp/client';
import { Icon } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { formatRelativeDate } from '@whinerp/contracts/format';

/**
 * A list of audit entries, wherever it is shown (F-018).
 *
 * ONE component, used by the record's History and by Setup's audit log, so the
 * two cannot come to disagree about how a change reads.
 *
 * Two rules from the feature's own brief. Diffs are field-level before/after
 * pairs, never raw JSON - a reader who has to parse `{"status":"won"}` has
 * been made to do the diff themselves. And an agent's action is visually
 * distinguishable from a person's, because "did AI change this?" is the
 * question that brings people here.
 */
export function AuditEntries({
  entries,
  emptyLabel,
}: {
  entries: AuditTrailResult['entries'];
  emptyLabel: string;
}): JSX.Element {
  const t = useT();
  const format = useFormat();

  if (entries.length === 0) {
    return (
      <p data-audit-empty className="text-dense text-ink-meta">
        {emptyLabel}
      </p>
    );
  }

  return (
    <ul data-audit-entries className="flex flex-col gap-2">
      {entries.map((entry) => (
        <li
          key={entry.logId}
          data-audit-entry={entry.logId}
          data-actor-type={entry.actorType}
          className="flex flex-col gap-1 border-b border-b-rule pb-2"
        >
          <div className="flex flex-wrap items-center gap-2 text-dense text-ink">
            {/* An agent is marked, not merely labelled: the icon is what makes
                it findable by eye in a long list. */}
            <Icon
              name={entry.actorType === 'agent' ? 'Sparkle' : entry.actorType === 'system' ? 'Gear' : 'User'}
              size="inline"
              // The word beside it already names the actor, so the icon is a
              // mark rather than a label.
              decorative
              className={entry.actorType === 'agent' ? 'text-bronze-text' : 'text-ink-meta'}
            />
            <span>{entry.summary}</span>
            <span data-audit-actor className="text-meta text-ink-meta">
              {entry.actorName ?? t(`audit.actor.${entry.actorType}` as never)}
            </span>
            <span className="text-meta text-ink-meta">{formatRelativeDate(entry.at, format)}</span>
          </div>

          {entry.changes.length > 0 ? (
            <ul className="flex flex-col gap-0.5 ps-6">
              {entry.changes.map((change) => (
                <li
                  key={`${entry.logId}:${change.field}`}
                  data-audit-change={change.field}
                  className="text-meta text-ink-600"
                >
                  {t('audit.changed', {
                    values: {
                      field: change.field,
                      before: change.before ?? t('audit.wasEmpty'),
                      after: change.after ?? t('audit.wasEmpty'),
                    },
                  })}
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
